/**
 * The permanent picture album — US 02.01, pure DOM. Opened with P.
 * Filterable by rarity and secret status (city/character/tag filters gain
 * teeth when more content exists — the controls are structured for it).
 * Uncollected slots from played cities appear as rarity-only silhouettes.
 */
import { worldBus } from '@shared/bus.js';
import { callDialogue } from '@transport/api.js';

interface AlbumData {
  entries: {
    pictureId: string;
    cityId: string;
    collectedAt: string;
    wasSecret: boolean;
    meta: {
      name: string;
      rarity: string;
      character_name: string | null;
      collection_tags: string[];
    } | null;
  }[];
  silhouettes: { rarity: string }[];
  totalKnown: number;
  xp: number;
  level: number;
  metaMoney: number;
}

const RARITY_ORDER = [
  'common',
  'uncommon',
  'very_uncommon',
  'rare',
  'extremely_rare',
  'legendary',
  'mythical',
];
const RARITY_LABEL: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  very_uncommon: 'Very Uncommon',
  rare: 'Rare',
  extremely_rare: 'Extremely Rare',
  legendary: 'Legendary',
  mythical: 'Mythical',
};

export function initAlbumPanel(root: HTMLElement): void {
  const panel = document.createElement('div');
  panel.className = 'wca-album hidden';
  root.appendChild(panel);

  let open = false;
  let data: AlbumData | null = null;
  let rarityFilter: string | null = null;
  let secretsOnly = false;

  function render(): void {
    panel.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'wca-inventory-head';
    head.textContent = data
      ? `Album — Level ${data.level} · ${data.xp} XP · ⛃ ${data.metaMoney} meta`
      : 'Album';
    panel.appendChild(head);
    if (!data) return;

    const agg = document.createElement('p');
    agg.textContent = `Collection: ${data.entries.length} of ${data.totalKnown} known`;
    panel.appendChild(agg);

    // Filters (rarity + secret status).
    const filters = document.createElement('div');
    filters.className = 'wca-apt-toolbar';
    const mk = (label: string, active: boolean, onClick: () => void) => {
      const b = document.createElement('button');
      b.className = active ? 'wca-choice wca-apt-active' : 'wca-choice';
      b.textContent = label;
      b.addEventListener('click', onClick);
      return b;
    };
    filters.appendChild(
      mk('All', rarityFilter === null && !secretsOnly, () => {
        rarityFilter = null;
        secretsOnly = false;
        render();
      }),
    );
    for (const r of RARITY_ORDER) {
      if (!data.entries.some((e) => e.meta?.rarity === r)) continue;
      filters.appendChild(
        mk(RARITY_LABEL[r] ?? r, rarityFilter === r, () => {
          rarityFilter = rarityFilter === r ? null : r;
          render();
        }),
      );
    }
    if (data.entries.some((e) => e.wasSecret)) {
      filters.appendChild(
        mk('Secrets', secretsOnly, () => {
          secretsOnly = !secretsOnly;
          render();
        }),
      );
    }
    panel.appendChild(filters);

    // Owned entries: rarity shown as colour class + frame + text label.
    const list = document.createElement('div');
    list.className = 'wca-album-grid';
    const visible = data.entries.filter(
      (e) =>
        (rarityFilter === null || e.meta?.rarity === rarityFilter) && (!secretsOnly || e.wasSecret),
    );
    for (const e of visible) {
      const card = document.createElement('div');
      card.className = `wca-album-card wca-rarity-${e.meta?.rarity ?? 'common'}`;
      const name = document.createElement('strong');
      name.textContent = e.meta?.name ?? e.pictureId;
      const details = document.createElement('small');
      details.textContent = [
        RARITY_LABEL[e.meta?.rarity ?? ''] ?? e.meta?.rarity,
        e.meta?.character_name,
        `from ${e.cityId}`,
        new Date(e.collectedAt).toLocaleDateString(),
        e.wasSecret ? 'secret' : null,
      ]
        .filter(Boolean)
        .join(' · ');
      card.append(name, details);
      list.appendChild(card);
    }
    // Silhouettes: not yet collected, may return.
    for (const s of data.silhouettes) {
      const card = document.createElement('div');
      card.className = `wca-album-card wca-album-silhouette wca-rarity-${s.rarity}`;
      const name = document.createElement('strong');
      name.textContent = '???';
      const det = document.createElement('small');
      det.textContent = `${RARITY_LABEL[s.rarity] ?? s.rarity} · Not yet collected — may return`;
      card.append(name, det);
      list.appendChild(card);
    }
    panel.appendChild(list);
  }

  worldBus.on('ui-intent', ({ intent }) => {
    if (intent === 'OPEN_ALBUM') {
      open = !open;
      panel.classList.toggle('hidden', !open);
      if (open) {
        render();
        void callDialogue<AlbumData>({ op: 'album' }).then((r) => {
          if (r.ok) {
            data = r.result;
            if (open) render();
          }
        });
      }
    }
    if (intent === 'CANCEL' && open) {
      open = false;
      panel.classList.add('hidden');
    }
  });
}
